import LabeledTextField from '@src/components/CustomTextField/LabeledTextField';
import {
	Autocomplete,
	Box,
	Paper,
	IconButton,
	SelectChangeEvent,
	Typography,
	styled,
	Stack,
	Button,
} from '@mui/material';
import { FormikProps } from 'formik';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { ApplicationContext } from '@src/contexts/ApplicationContext';
import React, { useEffect, useState } from 'react';
import AutoComplete from '@src/components/CustomSelect/AutoComplete';

interface FormThreeProps {
	machines: any;
	setMachines: any;
}

const MachineCard = styled(Box)`
	border: 1px solid #e0e0e0;
	border-radius: 12px;
	padding: 12px;
	margin-bottom: 16px;
	position: relative;
	background: white;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
	transition: all 0.3s ease;
	flex-direction: column;

	&:hover {
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
	}
`;

const DeleteButton = styled(IconButton)`
	position: absolute;
	top: 12px;
	right: 12px;
	color: #666;
	transition: all 0.2s ease;

	&:hover {
		color: #d32f2f;
		background: rgba(211, 47, 47, 0.08);
	}
`;

const AddMachineButton = styled(Box)`
	display: flex;
	align-items: center;
	gap: 8px;
	cursor: pointer;
	color: var(--primary-color);
	margin-bottom: 80px;
	padding: 12px 16px;
	border: 2px dashed #e0e0e0;
	border-radius: 8px;
	transition: all 0.2s ease;

	&:hover {
		background: rgba(25, 118, 210, 0.04);
		border-color: var(--primary-color);
	}
`;

function MachineryDetails({ machines, setMachines }: FormThreeProps) {
	const { apiService } = React.useContext(ApplicationContext);

	const [machineBrandOptions, setMachineBrandOptions] = useState([]);
	const [machineModelOptions, setMachineModelOptions] = useState([]);

	function getMachineBrandOptions() {
		apiService?.getMachineBrands(
			(res) => {
				const brands = res?.data?.data?.manufacturerBrands?.map((brand: any) => ({
					id: brand.brand_id,
					label: brand.name,
					value: brand.name,
				}));
				setMachineBrandOptions(brands);
			},
			(err) => {
				console.log(err);
			},
			''
		);
	}

	function getMachineModelOptions() {
		apiService?.getMachineModels(
			(res) => {
				const models = res?.data?.data?.machineModels?.map((modelId: any) => ({
					id: modelId.model_id,
					label: modelId.name,
					value: modelId.name,
				}));
				setMachineModelOptions(models);
			},
			(err) => {
				console.log(err);
			},
			''
		);
	}

	useEffect(() => {
		getMachineBrandOptions();
		getMachineModelOptions();
	}, []);

	const removeMachine = (index: number) => {
		const machinesToUpdate = [...machines];
		machinesToUpdate.splice(index, 1);
		setMachines(machinesToUpdate);
	};

	const addMachine = () => {
		const machinesToUpdate = [...machines];
		machinesToUpdate.push({
			machineBrandId: '',
			modelId: '',
			manufacturedYear: '',
			hoursRun: '',
		});
		setMachines(machinesToUpdate);
	};

	function validateManufacturedYear(manufacturedYear: string) {
		if (
			(manufacturedYear.length && manufacturedYear.length !== 4) ||
			isNaN(Number(manufacturedYear))
		) {
			return 'Manufactured year must be 4 digits';
		}
		return null;
	}

	function validateHoursRun(hoursRun: string) {
		if (hoursRun.length && (isNaN(Number(hoursRun)) || Number(hoursRun) < 0)) {
			return 'Hours run must be a number and greater than 0';
		}
		return null;
	}

	return (
		<Box display="flex" flexDirection="column">
			{machines.map((machine: any, index: number) => (
				<MachineCard key={index}>
					<Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
						<Typography fontSize={16} fontWeight={700}>
							Machine {index + 1}
						</Typography>
						<IconButton onClick={() => removeMachine(index)}>
							<DeleteIcon />
						</IconButton>
					</Stack>

					<Box display="flex" flexDirection="column" gap={2}>
						<AutoComplete
							selectedOption={
								machineBrandOptions?.find((option) => option.id === machine.machineBrandId) || null
							}
							options={machineBrandOptions || []}
							onChange={(newValue) => {
								const machinesToUpdate = [...machines];
								machinesToUpdate[index] = {
									...machinesToUpdate[index],
									machineBrandId: newValue?.id || '',
								};
								setMachines(machinesToUpdate);
							}}
							label="Machine Brand"
							// error={Boolean(machines[index]?.machineBrandId && machines[index]?.machineBrandId)}
						/>
						<AutoComplete
							selectedOption={
								machineModelOptions?.find((option) => option.id === machine.modelId) || null
							}
							options={machineModelOptions || []}
							onChange={(newValue) => {
								const machinesToUpdate = [...machines];
								machinesToUpdate[index] = {
									...machinesToUpdate[index],
									modelId: newValue?.id || '',
								};
								setMachines(machinesToUpdate);
							}}
							label="Machine Model"
							// error={Boolean(machines[index]?.modelId && machines[index]?.modelId)}
						/>

						<LabeledTextField
							label="Year of Manufacture"
							value={machine.manufacturedYear}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
								const machinesToUpdate = [...machines];
								machinesToUpdate[index] = {
									...machinesToUpdate[index],
									manufacturedYear: e.target.value,
								};
								setMachines(machinesToUpdate);
							}}
							name={`machines.${index}.manufacturedYear`}
							error={validateManufacturedYear(machine.manufacturedYear) ? true : false}
							errorText={validateManufacturedYear(machine.manufacturedYear)}
							styles={{ width: '100%' }}
						/>

						<LabeledTextField
							label="Number of Hours Run"
							value={machine.hoursRun}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
								const machinesToUpdate = [...machines];
								machinesToUpdate[index] = {
									...machinesToUpdate[index],
									hoursRun: e.target.value,
								};
								setMachines(machinesToUpdate);
							}}
							name={`machines.${index}.hoursRun`}
							error={validateHoursRun(machine.hoursRun) ? true : false}
							errorText={validateHoursRun(machine.hoursRun)}
							styles={{ width: '100%' }}
						/>
					</Box>
				</MachineCard>
			))}
			<AddMachineButton onClick={addMachine}>
				<AddIcon />
				<Typography>Add Another Machine</Typography>
			</AddMachineButton>
		</Box>
	);
}

export default MachineryDetails;
